import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Printer, 
  Check, 
  X, 
  AlertCircle, 
  Upload,
  Calendar,
  FileImage,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Crop,
  ZoomIn,
  Info,
  ExternalLink
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import './ArtistPrintOptinPage.css';

/**
 * ArtistPrintOptinPage - Public page for artists to opt-in to print campaigns
 * Accessed via magic link: /prints/optin/:optinUuid
 * 
 * Features:
 * - View campaign details and terms
 * - See eligible artworks with quality ratings
 * - Upload high-res images if needed
 * - Select signing event availability
 * - Accept or decline participation
 */
const ArtistPrintOptinPage = () => {
  const { optinUuid } = useParams();
  const navigate = useNavigate();

  // Data state
  const [optinRecord, setOptinRecord] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [qualityStatus, setQualityStatus] = useState({});
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedArtwork, setExpandedArtwork] = useState(null);
  const [uploadingArtworkId, setUploadingArtworkId] = useState(null);
  
  // Form state
  const [termsAccepted, setTermsAccepted] = useState({
    printRights: false,
    qualityAcknowledged: false,
    pricingAgreed: false,
    fulfillmentConsent: false
  });
  const [signingAvailability, setSigningAvailability] = useState({});
  const [needsHighresAssistance, setNeedsHighresAssistance] = useState(false);
  const [showTermsDetail, setShowTermsDetail] = useState(false);

  // Minimum dimensions for print sizes at 150 DPI (acceptable quality)
  const MIN_DIMENSIONS = {
    '11x14': { width: 1650, height: 2100, label: '11×14"' },
    '12x12': { width: 1800, height: 1800, label: '12×12"' },
    '16x20': { width: 2400, height: 3000, label: '16×20"' },
    '18x18': { width: 2700, height: 2700, label: '18×18"' },
  };

  // Build optimized Cloudinary URL
  const getOptimizedImageUrl = (url, width = 400) => {
    if (!url) return '/images/placeholder-artwork.jpg';
    
    if (url.includes('cloudinary.com') && url.includes('/image/upload/')) {
      return url.replace(
        '/image/upload/',
        `/image/upload/w_${width},c_limit,q_auto,f_auto/`
      );
    }
    
    return url;
  };

  // Load all page data
  const loadPageData = useCallback(async () => {
    if (!optinUuid) {
      setError('Invalid opt-in link');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 1. Load the opt-in record
      const { data: optinData, error: optinError } = await supabase
        .from('artist_print_optin')
        .select('*')
        .eq('optin_uuid', optinUuid)
        .single();

      if (optinError || !optinData) {
        throw new Error('Opt-in record not found. This link may be invalid or expired.');
      }

      setOptinRecord(optinData);

      // Check if already accepted
      if (optinData.optin_accepted) {
        // Still load data but show confirmation state
      }

      // 2. Load the campaign
      const { data: campaignData, error: campaignError } = await supabase
        .from('print_campaigns')
        .select('*')
        .eq('campaign_id', optinData.campaign_id)
        .single();

      if (campaignError || !campaignData) {
        throw new Error('Campaign not found');
      }

      setCampaign(campaignData);

      // Initialize signing availability from existing data or campaign dates
      if (optinData.signing_availability) {
        setSigningAvailability(optinData.signing_availability);
      } else if (campaignData.signing_event_dates) {
        const dates = campaignData.signing_event_dates;
        const initialAvailability = {};
        dates.forEach(date => {
          initialAvailability[date.date] = false;
        });
        setSigningAvailability(initialAvailability);
      }

      // 3. Load artist info
      const { data: artistData, error: artistError } = await supabase
        .from('users')
        .select('uuid, email, first_name, last_name, handle, profile_img')
        .eq('uuid', optinData.artist_uuid)
        .single();

      if (!artistError && artistData) {
        setArtist(artistData);
      }

      // 4. Load artist's artworks in this collection
      const { data: mappingData, error: mappingError } = await supabase
        .from('collection_vc_mapping')
        .select(`
          vc_id,
          accepted,
          vcs (
            vc_id,
            title,
            cover_image,
            cloudinary_url,
            width,
            height,
            uuid
          )
        `)
        .eq('collection_id', campaignData.collection_id)
        .eq('accepted', true);

      if (!mappingError && mappingData) {
        // Filter to only this artist's artworks
        const artistArtworks = mappingData
          .filter(m => m.vcs && m.vcs.uuid === optinData.artist_uuid)
          .map(m => m.vcs);
        
        setArtworks(artistArtworks);

        // 5. Load quality status for these artworks
        if (artistArtworks.length > 0) {
          const vcIds = artistArtworks.map(a => a.vc_id);
          const { data: qualityData, error: qualityError } = await supabase
            .from('artwork_print_status')
            .select('*')
            .in('vc_id', vcIds)
            .is('optin_id', null); // Get artwork-level quality, not artist-specific

          if (!qualityError && qualityData) {
            const qualityMap = {};
            qualityData.forEach(q => {
              qualityMap[q.vc_id] = q;
            });
            setQualityStatus(qualityMap);
          }
        }
      }

      // Load existing terms acceptance if any
      if (optinData.terms_accepted) {
        setTermsAccepted(optinData.terms_accepted);
      }

      if (optinData.needs_highres_assistance) {
        setNeedsHighresAssistance(optinData.needs_highres_assistance);
      }

    } catch (err) {
      console.error('Error loading opt-in page data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [optinUuid]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // Get quality badge info
  const getQualityBadge = (qualityLevel) => {
    switch (qualityLevel) {
      case 'excellent':
        return { color: '#16a34a', bgColor: '#dcfce7', icon: <CheckCircle size={14} />, label: 'Excellent' };
      case 'good':
        return { color: '#2563eb', bgColor: '#dbeafe', icon: <Check size={14} />, label: 'Good' };
      case 'acceptable':
        return { color: '#ca8a04', bgColor: '#fef9c3', icon: <Clock size={14} />, label: 'Acceptable' };
      case 'low':
        return { color: '#dc2626', bgColor: '#fee2e2', icon: <X size={14} />, label: 'Low Quality' };
      default:
        return { color: '#6b7280', bgColor: '#f3f4f6', icon: <AlertCircle size={14} />, label: 'Not Scanned' };
    }
  };

  // Get available print sizes for an artwork
  const getAvailableSizes = (quality) => {
    if (!quality || !quality.available_sizes) return [];
    return Object.entries(quality.available_sizes)
      .filter(([_, info]) => info.available)
      .map(([size, info]) => ({
        size,
        label: MIN_DIMENSIONS[size]?.label || size,
        quality: info.quality
      }));
  };

  // Check if artwork needs high-res upload
  const needsHighresUpload = (vcId) => {
    const quality = qualityStatus[vcId];
    if (!quality) return true;
    return quality.quality_level === 'low' || !quality.quality_level;
  };

  // Handle high-res image upload
  const handleHighresUpload = async (vcId, file) => {
    if (!file) return;

    setUploadingArtworkId(vcId);

    try {
      // Create form data for Cloudinary upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', 'vibrantcast_unsigned');
      formData.append('folder', 'highres_prints');

      // Upload to Cloudinary
      const cloudinaryResponse = await fetch(
        'https://api.cloudinary.com/v1_1/vibrantcast/image/upload',
        {
          method: 'POST',
          body: formData
        }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Failed to upload image');
      }

      const cloudinaryData = await cloudinaryResponse.json();

      // Update or create artwork_print_status with high-res URL
      const { error: updateError } = await supabase
        .from('artwork_print_status')
        .upsert({
          vc_id: vcId,
          optin_id: optinRecord.id,
          highres_image_url: cloudinaryData.secure_url,
          image_width: cloudinaryData.width,
          image_height: cloudinaryData.height,
          image_bytes: cloudinaryData.bytes,
          quality_checked_at: new Date().toISOString(),
          quality_level: calculateQualityLevel(cloudinaryData.width, cloudinaryData.height),
          available_sizes: calculateAvailableSizes(cloudinaryData.width, cloudinaryData.height)
        }, {
          onConflict: 'vc_id,optin_id'
        });

      if (updateError) throw updateError;

      // Reload quality data
      await loadPageData();

      alert('High-resolution image uploaded successfully!');

    } catch (err) {
      console.error('Error uploading high-res image:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploadingArtworkId(null);
    }
  };

  // Calculate quality level based on dimensions
  const calculateQualityLevel = (width, height) => {
    const maxDim = Math.max(width, height);
    if (maxDim >= 5400) return 'excellent';
    if (maxDim >= 3000) return 'good';
    if (maxDim >= 1800) return 'acceptable';
    return 'low';
  };

  // Calculate available sizes based on dimensions
  const calculateAvailableSizes = (width, height) => {
    const sizes = {};
    Object.entries(MIN_DIMENSIONS).forEach(([size, dims]) => {
      const meetsMin = width >= dims.width && height >= dims.height;
      const meetsExcellent = width >= dims.width * 2 && height >= dims.height * 2;
      sizes[size] = {
        available: meetsMin,
        quality: meetsExcellent ? 'excellent' : meetsMin ? 'good' : 'insufficient'
      };
    });
    return sizes;
  };

  // Check if all required terms are accepted
  const allTermsAccepted = () => {
    return termsAccepted.printRights && 
           termsAccepted.qualityAcknowledged && 
           termsAccepted.pricingAgreed && 
           termsAccepted.fulfillmentConsent;
  };

  // Handle opt-in submission
  const handleAcceptOptin = async () => {
    if (!allTermsAccepted()) {
      alert('Please accept all terms to continue');
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('artist_print_optin')
        .update({
          optin_accepted: true,
          optin_at: new Date().toISOString(),
          terms_accepted: termsAccepted,
          signing_availability: signingAvailability,
          needs_highres_assistance: needsHighresAssistance,
          optin_ip: null, // Would need server-side to get real IP
          optin_user_agent: navigator.userAgent
        })
        .eq('optin_uuid', optinUuid);

      if (error) throw error;

      // Reload to show confirmation state
      await loadPageData();

    } catch (err) {
      console.error('Error accepting opt-in:', err);
      alert('Failed to submit: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle decline
  const handleDecline = async () => {
    if (!confirm('Are you sure you want to decline participation in this print campaign?')) {
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('artist_print_optin')
        .update({
          optin_accepted: false,
          optin_at: new Date().toISOString(),
          terms_accepted: { declined: true }
        })
        .eq('optin_uuid', optinUuid);

      if (error) throw error;

      alert('You have declined participation in this campaign.');
      // Could redirect to a thank you page

    } catch (err) {
      console.error('Error declining opt-in:', err);
      alert('Failed to submit: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="artist-print-optin-page loading">
        <div className="loading-spinner"></div>
        <span>Loading campaign details...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="artist-print-optin-page error">
        <AlertCircle size={48} />
        <h3>Unable to Load</h3>
        <p>{error}</p>
        <button onClick={() => navigate('/')}>Return Home</button>
      </div>
    );
  }

  // Already accepted state
  if (optinRecord?.optin_accepted) {
    return (
      <div className="artist-print-optin-page success">
        <div className="success-container">
          <CheckCircle size={64} className="success-icon" />
          <h2>You're All Set!</h2>
          <p>You've successfully opted in to the <strong>{campaign?.title}</strong> print campaign.</p>
          
          <div className="confirmation-details">
            <h4>What happens next?</h4>
            <ul>
              <li>We'll prepare your artwork for high-quality print production</li>
              <li>You'll receive a notification when prints go on sale</li>
              {Object.entries(signingAvailability).some(([_, available]) => available) && (
                <li>You're confirmed for the signing event on your selected date(s)</li>
              )}
            </ul>
          </div>

          <div className="confirmed-artworks">
            <h4>Your Artworks in This Campaign</h4>
            <div className="artwork-grid">
              {artworks.map(artwork => (
                <div key={artwork.vc_id} className="confirmed-artwork-card">
                  <img 
                    src={getOptimizedImageUrl(artwork.cover_image || artwork.cloudinary_url, 200)} 
                    alt={artwork.title}
                  />
                  <span>{artwork.title}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="return-button" onClick={() => navigate('/')}>
            Return to VibrantCast
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="artist-print-optin-page">
      {/* Header */}
      <header className="optin-header">
        <div className="header-content">
          <Printer size={32} />
          <div>
            <h1>{campaign?.title || 'Print Campaign'}</h1>
            {campaign?.subtitle && <p className="subtitle">{campaign.subtitle}</p>}
          </div>
        </div>
      </header>

      {/* Artist Welcome */}
      <section className="artist-welcome">
        <div className="welcome-content">
          <h2>Welcome, {artist?.first_name || 'Artist'}!</h2>
          <p>
            You've been selected to participate in our exclusive print campaign. 
            Your artwork has been curated for high-quality giclée prints that collectors can purchase.
          </p>
        </div>
      </section>

      {/* Artworks Section */}
      <section className="artworks-section">
        <h3>
          <ImageIcon size={20} />
          Your Selected Artworks ({artworks.length})
        </h3>
        <p className="section-description">
          These artworks have been selected for the print campaign. Review the quality status for each.
        </p>

        <div className="artworks-list">
          {artworks.map(artwork => {
            const quality = qualityStatus[artwork.vc_id];
            const badge = getQualityBadge(quality?.quality_level);
            const availableSizes = getAvailableSizes(quality);
            const needsUpload = needsHighresUpload(artwork.vc_id);
            const isExpanded = expandedArtwork === artwork.vc_id;

            return (
              <div key={artwork.vc_id} className={`artwork-card ${needsUpload ? 'needs-attention' : ''}`}>
                <div className="artwork-main" onClick={() => setExpandedArtwork(isExpanded ? null : artwork.vc_id)}>
                  <div className="artwork-image">
                    <img 
                      src={getOptimizedImageUrl(artwork.cover_image || artwork.cloudinary_url, 300)} 
                      alt={artwork.title}
                    />
                  </div>
                  
                  <div className="artwork-info">
                    <h4>{artwork.title}</h4>
                    
                    <div className="quality-badge" style={{ backgroundColor: badge.bgColor, color: badge.color }}>
                      {badge.icon}
                      <span>{badge.label}</span>
                    </div>

                    {quality && (
                      <div className="dimensions-info">
                        <span>Current: {quality.image_width} × {quality.image_height} px</span>
                      </div>
                    )}

                    {availableSizes.length > 0 && (
                      <div className="available-sizes">
                        <span className="sizes-label">Available sizes:</span>
                        {availableSizes.map(s => (
                          <span key={s.size} className="size-chip">{s.label}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="expand-toggle">
                    {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="artwork-expanded">
                    {needsUpload && (
                      <div className="upload-prompt">
                        <AlertCircle size={20} />
                        <div>
                          <strong>Higher Resolution Needed</strong>
                          <p>Upload a higher resolution image for the best print quality.</p>
                        </div>
                      </div>
                    )}

                    <div className="upload-section">
                      <h5>
                        <Upload size={16} />
                        Upload High-Resolution Image
                      </h5>
                      <p>For best results, upload an image at least 4800 × 6000 pixels (300 DPI at 16×20").</p>
                      
                      <div className="upload-dropzone">
                        <input
                          type="file"
                          id={`upload-${artwork.vc_id}`}
                          accept="image/jpeg,image/png,image/tiff"
                          onChange={(e) => handleHighresUpload(artwork.vc_id, e.target.files?.[0])}
                          disabled={uploadingArtworkId === artwork.vc_id}
                        />
                        <label htmlFor={`upload-${artwork.vc_id}`}>
                          {uploadingArtworkId === artwork.vc_id ? (
                            <>
                              <div className="uploading-spinner"></div>
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <FileImage size={24} />
                              <span>Click to upload or drag & drop</span>
                              <small>JPEG, PNG, or TIFF • Max 50MB</small>
                            </>
                          )}
                        </label>
                      </div>

                      {quality?.highres_image_url && (
                        <div className="highres-uploaded">
                          <CheckCircle size={16} />
                          <span>High-resolution image uploaded</span>
                          <a href={quality.highres_image_url} target="_blank" rel="noopener noreferrer">
                            View <ExternalLink size={12} />
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="size-requirements">
                      <h5>Print Size Requirements</h5>
                      <div className="requirements-grid">
                        {Object.entries(MIN_DIMENSIONS).map(([size, dims]) => {
                          const sizeQuality = quality?.available_sizes?.[size];
                          return (
                            <div 
                              key={size} 
                              className={`requirement-item ${sizeQuality?.available ? 'available' : 'unavailable'}`}
                            >
                              <span className="size-label">{dims.label}</span>
                              <span className="size-dims">{dims.width} × {dims.height} px</span>
                              {sizeQuality?.available ? (
                                <Check size={14} className="check" />
                              ) : (
                                <X size={14} className="x" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Assistance checkbox */}
        <div className="assistance-option">
          <label>
            <input
              type="checkbox"
              checked={needsHighresAssistance}
              onChange={(e) => setNeedsHighresAssistance(e.target.checked)}
            />
            <span>I need help obtaining high-resolution images of my artwork</span>
          </label>
          <p className="help-text">
            Our team can assist with professional scanning or photography if needed.
          </p>
        </div>
      </section>

      {/* Signing Events Section */}
      {campaign?.signing_event_dates && campaign.signing_event_dates.length > 0 && (
        <section className="signing-section">
          <h3>
            <Calendar size={20} />
            Signing Event Availability
          </h3>
          <p className="section-description">
            Select which dates you're available for the print signing event. 
            Collectors love meeting artists!
          </p>

          <div className="signing-dates">
            {campaign.signing_event_dates.map((event, idx) => (
              <label key={idx} className="signing-date-option">
                <input
                  type="checkbox"
                  checked={signingAvailability[event.date] || false}
                  onChange={(e) => setSigningAvailability(prev => ({
                    ...prev,
                    [event.date]: e.target.checked
                  }))}
                />
                <div className="date-info">
                  <strong>{formatDate(event.date)}</strong>
                  {event.time && <span>{event.time}</span>}
                  {event.location && <span className="location">{event.location}</span>}
                </div>
              </label>
            ))}
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {campaign?.pricing && (
        <section className="pricing-section">
          <h3>Print Pricing & Your Earnings</h3>
          <p className="section-description">
            Here's how print sales work and what you'll earn.
          </p>

          <div className="pricing-grid">
            {Object.entries(campaign.pricing).map(([size, info]) => (
              <div key={size} className="pricing-card">
                <span className="size">{MIN_DIMENSIONS[size]?.label || size}</span>
                <span className="price">${info.price}</span>
                <span className="artist-earnings">You earn: ${info.artistShare || Math.round(info.price * 0.4)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Terms Section */}
      <section className="terms-section">
        <h3>
          <Info size={20} />
          Terms & Conditions
        </h3>
        
        <button 
          className="terms-toggle"
          onClick={() => setShowTermsDetail(!showTermsDetail)}
        >
          {showTermsDetail ? 'Hide Full Terms' : 'View Full Terms'}
          {showTermsDetail ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showTermsDetail && campaign?.optin_terms && (
          <div 
            className="terms-content"
            dangerouslySetInnerHTML={{ __html: campaign.optin_terms }}
          />
        )}

        <div className="terms-checkboxes">
          <label className={termsAccepted.printRights ? 'checked' : ''}>
            <input
              type="checkbox"
              checked={termsAccepted.printRights}
              onChange={(e) => setTermsAccepted(prev => ({ ...prev, printRights: e.target.checked }))}
            />
            <span>I grant VibrantCast the right to produce and sell prints of my selected artwork during this campaign.</span>
          </label>

          <label className={termsAccepted.qualityAcknowledged ? 'checked' : ''}>
            <input
              type="checkbox"
              checked={termsAccepted.qualityAcknowledged}
              onChange={(e) => setTermsAccepted(prev => ({ ...prev, qualityAcknowledged: e.target.checked }))}
            />
            <span>I understand that print quality depends on image resolution and have uploaded the highest quality images available.</span>
          </label>

          <label className={termsAccepted.pricingAgreed ? 'checked' : ''}>
            <input
              type="checkbox"
              checked={termsAccepted.pricingAgreed}
              onChange={(e) => setTermsAccepted(prev => ({ ...prev, pricingAgreed: e.target.checked }))}
            />
            <span>I agree to the pricing structure and artist earnings percentage shown above.</span>
          </label>

          <label className={termsAccepted.fulfillmentConsent ? 'checked' : ''}>
            <input
              type="checkbox"
              checked={termsAccepted.fulfillmentConsent}
              onChange={(e) => setTermsAccepted(prev => ({ ...prev, fulfillmentConsent: e.target.checked }))}
            />
            <span>I consent to VibrantCast handling all print production, shipping, and customer service on my behalf.</span>
          </label>
        </div>
      </section>

      {/* Action Buttons */}
      <section className="action-section">
        <button 
          className="accept-button"
          onClick={handleAcceptOptin}
          disabled={submitting || !allTermsAccepted()}
        >
          {submitting ? 'Submitting...' : 'Accept & Join Campaign'}
          <CheckCircle size={18} />
        </button>

        <button 
          className="decline-button"
          onClick={handleDecline}
          disabled={submitting}
        >
          Decline Participation
        </button>

        <p className="action-note">
          By accepting, you agree to participate in this print campaign under the terms above.
        </p>
      </section>

      {/* Footer */}
      <footer className="optin-footer">
        <p>Questions? Contact us at <a href="mailto:support@vibrantcast.com">support@vibrantcast.com</a></p>
      </footer>
    </div>
  );
};

export default ArtistPrintOptinPage;
